import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import DataTable from "../../components/carbon/DataTable/DataTable";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import {
  useAllCompanyMaterialOffers,
  useAllMaterialOffers,
} from "../../hooks/useMaterialsOffers";
import { useUser } from "../../context";
import { MaterialOffersPaginatedResponse } from "../../types";
import {
  buildMaterialOfferRows,
  materialOfferHeaders,
  navigateToMaterialOffer,
} from "./materialOfferRows";
import "./materialOffers.scss";

const PAGE_SIZE_FALLBACK = 10;

/**
 * Seller-side "Material Offers" list (applications to sell a material, distinct from an
 * order/RFQ offer), rebuilt on the Carbon primitives layer (sub-flow C, Task 4). Two tables: the
 * caller's own applications, and — for non-analysts — every application the whole company made.
 * `MaterialOfferTable`/`MaterialOfferTableRow` (Tailwind, seller-list-only — grepped: no consumer
 * outside this container) are replaced by `carbon/DataTable`; `materialOfferRows.tsx` carries the
 * row/header builders, same split `orderListRows.tsx` established for Orders.tsx.
 */
const MaterialOffers = () => {
  const { isAnalyst } = useUser();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [pageNumberMaterialOffer, setPageNumberMaterialOffer] = useState(0);
  const [pageNumberCompanyMaterialOffer, setPageNumberCompanyMaterialOffer] =
    useState(0);

  // Neither hook has a generic on its own `useQuery` call (matches the pre-Carbon container,
  // which also read `.content` off an untyped `any`) — asserted here to the real wire shape.
  const { data: allMaterialsOffers, isLoading: isLoadingAllMaterialOffers } =
    useAllMaterialOffers(pageNumberMaterialOffer) as {
      data?: MaterialOffersPaginatedResponse;
      isLoading: boolean;
    };

  const {
    data: allCompanyMaterialOffers,
    isLoading: isLoadingAllMaterialCompanyOffers,
  } = useAllCompanyMaterialOffers(
    pageNumberCompanyMaterialOffer,
    isAnalyst
  ) as {
    data?: MaterialOffersPaginatedResponse;
    isLoading: boolean;
  };

  const rows = buildMaterialOfferRows(
    allMaterialsOffers?.content ?? [],
    false,
    t
  );
  const companyRows = buildMaterialOfferRows(
    allCompanyMaterialOffers?.content ?? [],
    true,
    t
  );

  return (
    <div>
      <PageHeader title={t("navigation.materialOffers")} />

      {isLoadingAllMaterialOffers || rows.length > 0 ? (
        <DataTable
          headers={materialOfferHeaders(t, false)}
          rows={rows}
          isLoading={isLoadingAllMaterialOffers}
          onRowClick={(row) => navigateToMaterialOffer(navigate, row)}
          pagination={{
            page: pageNumberMaterialOffer + 1,
            pageSize: allMaterialsOffers?.size || PAGE_SIZE_FALLBACK,
            totalItems: allMaterialsOffers?.totalElements ?? 0,
            onPageChange: (newPage) => setPageNumberMaterialOffer(newPage - 1),
          }}
          empty={<EmptyState title={t("opportunities.materialOffers")} />}
        />
      ) : (
        <EmptyState title={t("opportunities.materialOffers")} />
      )}

      {!isAnalyst &&
      (isLoadingAllMaterialCompanyOffers || companyRows.length > 0) ? (
        <>
          <h2 className="material-offers__section-title">
            {t("offer.companyApplications")}
          </h2>
          <DataTable
            headers={materialOfferHeaders(t, true)}
            rows={companyRows}
            isLoading={isLoadingAllMaterialCompanyOffers}
            onRowClick={(row) => navigateToMaterialOffer(navigate, row)}
            pagination={{
              page: pageNumberCompanyMaterialOffer + 1,
              pageSize: allCompanyMaterialOffers?.size || PAGE_SIZE_FALLBACK,
              totalItems: allCompanyMaterialOffers?.totalElements ?? 0,
              onPageChange: (newPage) =>
                setPageNumberCompanyMaterialOffer(newPage - 1),
            }}
            empty={<EmptyState title={t("opportunities.materialOffers")} />}
          />
        </>
      ) : null}
    </div>
  );
};

export default MaterialOffers;
